import { Box, Typography } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { FC, useCallback } from "react";
import styled from "styled-components";
import { Menu } from "client/jspPlatformExperiment";
import { isMenuItemBasicWithSides } from "models/menu/menu";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import { menusInWeekActions } from "store/slices/menu/menusInWeekSlice";
import menusInWeekSelector from "store/selectors/menu/menusInWeekSelector";
import dayjs from "dayjs";
import { foodPurchasingGuideActions } from "store/slices/inventory/foodPurchasingGuideSlice";

const subtitleBg = {
  lunch: "#ffe066", // yellow for lunch
  dinner: "#b3e5fc", // blue for dinner
  lunchHover: "#fdeeb0", // lighter yellow for lunch hover
  dinnerHover: "#ddf1fa", // lighter blue for dinner hover
};

interface MenuCellContentBoxProps {
  selected?: boolean;
  mealType: "lunch" | "dinner";
}

const MenuCellContentBox = styled(Box).withConfig({
  shouldForwardProp: (prop) => !["selected", "mealType"].includes(prop),
})<MenuCellContentBoxProps>`
  padding: 0;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  transition: border-color 0.2s;

  &:hover {
    border-color: #8e8e8e;
    background-color: ${({ selected, mealType }) => {
      if (!selected) {
        return "#f5f5f5";
      }
      return mealType === "lunch"
        ? subtitleBg.lunchHover
        : subtitleBg.dinnerHover;
    }};
  }

  background-color: ${({ selected, mealType }) => {
    if (!selected) {
      return "transparent";
    }
    return mealType === "lunch" ? subtitleBg.lunch : subtitleBg.dinner;
  }};
  border: ${({ selected }) =>
    selected ? "1px solid white" : "1px solid transparent"};
`;

const MenuCellSectionBox = styled(Box)`
  flex: 1;
  padding: 0;
`;

interface MenuColorCodeProps {
  mealType: "lunch" | "dinner";
  selected?: boolean;
}

const MenuCellSectionTitleBox = styled(Box).withConfig({
  shouldForwardProp: (prop) => !["mealType", "selected"].includes(prop),
})<MenuColorCodeProps>`
  width: calc(100% - 0.5rem);
  padding: 0.25rem;
  text-align: center;
  border-radius: 0.5rem;
`;

const MenuCellSectionTitleTypography = styled(Typography).withConfig({
  shouldForwardProp: (prop) => !["mealType", "selected"].includes(prop),
})<MenuColorCodeProps>`
  font-weight: 100;
  font-size: 0.8rem;
  text-transform: uppercase;
  border-radius: 0.5rem;
  padding: 0.25rem 1rem;
  background-color: ${({ mealType, selected }) => {
    if (selected) {
      return mealType === "lunch"
        ? subtitleBg.lunchHover
        : subtitleBg.dinnerHover;
    }
    return mealType === "lunch" ? subtitleBg.lunch : subtitleBg.dinner;
  }};
`;

const MenuCellSectionContentBox = styled(Box)`
  width: 100%;
  padding: 0.5rem 0 1rem;
`;

const MenuCellSectionItemBox = styled(Box)`
  width: calc(100% - 0.5rem);
  padding-bottom: 0.25rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  text-align: center;
`;

interface MenuCellProps {
  mealType: "lunch" | "dinner";
  menuData: Menu;
}

const MenuCell: FC<MenuCellProps> = ({ mealType, menuData }) => {
  const dispatch = useAppDispatch();
  const selectedMenusByDate = useAppSelector(
    menusInWeekSelector.selectedMenusByDate,
  );
  const selectedMenuIds =
    selectedMenusByDate[
      dayjs(menuData.serving_datetime).format("YYYY-MM-DD")
    ] || [];
  const isSelected = selectedMenuIds.includes(menuData.id);

  const handleClick = useCallback(() => {
    dispatch(
      menusInWeekActions.toggleSelectedMenusByDate({
        date: dayjs(menuData.serving_datetime).format("YYYY-MM-DD"),
        menuId: menuData.id,
      }),
    );
    dispatch(foodPurchasingGuideActions.setFoodPurchasingGuide(null));
  }, [dispatch, menuData]);

  return (
    <MenuCellContentBox
      mealType={mealType}
      selected={isSelected}
      onClick={handleClick}
    >
      {menuData.sections.map((section) => (
        <MenuCellSectionBox key={uuidv4()}>
          <MenuCellSectionTitleBox mealType={mealType}>
            <MenuCellSectionTitleTypography
              variant="caption"
              color="text.secondary"
              mealType={mealType}
              selected={isSelected}
            >
              {section.name}
            </MenuCellSectionTitleTypography>
          </MenuCellSectionTitleBox>
          <MenuCellSectionContentBox>
            {section.options.map((item) => (
              <MenuCellSectionItemBox key={uuidv4()}>
                <Typography variant="body2">
                  {isMenuItemBasicWithSides(item)
                    ? item.display_name
                    : item.name}
                </Typography>
              </MenuCellSectionItemBox>
            ))}
          </MenuCellSectionContentBox>
        </MenuCellSectionBox>
      ))}
    </MenuCellContentBox>
  );
};

export default MenuCell;
